import React, { useEffect, useState } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { BRAND } from "../styles/authTheme";

const RESET_DELAY_MS = 3000;

const PunchSuccess = ({ employeeName, action, timestamp, queued = false }) => {
  const [countdown, setCountdown] = useState(Math.ceil(RESET_DELAY_MS / 1000));
  const [mounted, setMounted] = useState(false);
  const actionLabel = action === "check_in" ? "Punched In" : "Punched Out";
  const isCheckIn = action === "check_in";
  const timeLabel = timestamp
    ? new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const progress = ((RESET_DELAY_MS / 1000 - countdown) / (RESET_DELAY_MS / 1000)) * 100;
  const accent = queued ? "#D97706" : isCheckIn ? BRAND.success : BRAND.navy;

  return (
    <Box
      sx={{
        textAlign: "center",
        py: { xs: 2.5, sm: 3 },
        px: 1,
        opacity: mounted ? 1 : 0,
        transform: mounted ? "scale(1)" : "scale(0.96)",
        transition: "opacity 0.25s ease, transform 0.25s ease",
      }}
    >
      <Box
        sx={{
          width: { xs: 72, sm: 80 },
          height: { xs: 72, sm: 80 },
          borderRadius: "50%",
          bgcolor: queued ? "rgba(251, 191, 36, 0.15)" : `${accent}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2,
          boxShadow: `0 8px 24px ${accent}33`,
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: accent }} />
      </Box>

      <Typography
        variant="h5"
        sx={{
          color: BRAND.navy,
          fontWeight: 800,
          mb: 0.5,
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
        }}
      >
        {actionLabel}!
      </Typography>

      {employeeName && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: BRAND.text,
            mb: 0.5,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          {employeeName}
        </Typography>
      )}

      {timeLabel && (
        <Typography variant="body1" sx={{ color: BRAND.muted, mb: queued ? 1 : 2 }}>
          at {timeLabel}
        </Typography>
      )}

      {queued && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: "#D97706",
            bgcolor: "rgba(251, 191, 36, 0.12)",
            py: 1,
            px: 2,
            borderRadius: "8px",
            display: "inline-block",
          }}
        >
          Saved offline — will sync when connected
        </Typography>
      )}

      <Box sx={{ maxWidth: 280, mx: "auto" }}>
        <Typography variant="caption" sx={{ color: BRAND.muted, mb: 0.75, display: "block" }}>
          Next worker in {countdown}s…
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: "#E5E7EB",
            "& .MuiLinearProgress-bar": {
              bgcolor: BRAND.orange,
              borderRadius: 3,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PunchSuccess;
